import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { BehaviorSubject, forkJoin, Observable, of, Subject } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { MultiSelectChipOption } from "src/app/components/multi-select-chips/multi-select-chips.component";
import { formatDate } from "src/app/i18next/formatDate";
import { QueryStrings } from "src/app/models/emitter-events.models";
import {
	DeleteRulesBody,
	DisplayedZone,
	MirRule,
	MirRuleCreateRequest,
	MirRuleHttp,
	MirRulePatchBody,
	RuleZone,
	TimeRange,
	TripStartOrEnd,
} from "src/app/models/mir-rule.model";
import { environment } from "src/environments/environment";
import { RequestService } from "../request/request.service";
import { ServiceUtilsService } from "../service-utils.service";

@Injectable({
	providedIn: "root",
})
export class RulesService {
	constructor(private requestService: RequestService, private serviceUtils: ServiceUtilsService) {}
	confirmEditRule$ = new Subject();
	confirmDeleteRules$ = new Subject();

	dispatchUpdatedRules$: BehaviorSubject<MirRuleHttp[]> = new BehaviorSubject(null);

	savedRule$: BehaviorSubject<MirRuleHttp> = new BehaviorSubject(null);

	ruleToPatch$: BehaviorSubject<MirRule> = new BehaviorSubject(null);

	startInspectionTypes$: BehaviorSubject<MultiSelectChipOption[]> = new BehaviorSubject(null);
	endInspectionTypes$: BehaviorSubject<MultiSelectChipOption[]> = new BehaviorSubject(null);
	// cache still wonky, not using it for release, we can refine it later.

	patchedRuleMap: Map<string, { [key: string]: MirRule }> = new Map();

	disableSaveButton$: BehaviorSubject<boolean> = new BehaviorSubject(null);
	private _intsToDaysMap = {
		0: "Sun",
		1: "Mon",
		2: "Tue",
		3: "Wed",
		4: "Thu",
		5: "Fri",
		6: "Sat",
	};

	formatRuleForDisplay(rule: MirRuleHttp): MirRule {
		return {
			name: rule.name || "Unknown",
			byUser: rule.lastUserName || "Unknown",
			assignedAssets: rule.assets?.length || 0,
			updated: rule.updated ? formatDate(new Date(rule.updated)) : "Unknown",
			id: rule.id,
			excludedZones: rule.excludeZones,
			tripStart: rule.tripStart,
			tripEnd: rule.tripEnd,
			companyId: rule.companyId,
			disableCheckbox: rule.assets?.length > 0,
			assets: rule.assets.slice(),
		};
	}
	getRules(companyId: string, query: QueryStrings) {
		const queryString = new URLSearchParams(query as any).toString();
		const url = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/rules?companyId=${companyId}&${queryString}`;

		return this.requestService.get<HttpResponse<MirRuleHttp[]>>({
			url,
			httpOptions: { observe: "response" },
		});
	}

	getAllRules(companyId: string) {
		let rules: MirRuleHttp[] = [];
		const params = {
			page: "1",
			perPage: "500",
			active: true,
			sort: "ruleName",
			sortDir: "asc",
		};

		return this.getRules(companyId, params).pipe(
			switchMap(response => {
				const totalPages = parseInt(response.headers.get("X-Page-Count"));
				rules = response.body;
				if (totalPages > 1) {
					const obs = [];
					for (let i = 2; i <= totalPages; i++) {
						params.page = i.toString();
						obs.push(this.getRules(companyId, params));
					}
					return forkJoin(obs).pipe(
						map(res => {
							res.forEach(r => {
								(r as any).body.forEach(item => {
									rules.push(item);
								});
							});
							this.dispatchUpdatedRules$.next(rules);
							return rules;
						}),
					);
				}

				this.dispatchUpdatedRules$.next(rules);

				return of(rules);
			}),
		);
	}

	patchRule(id: string, body: MirRulePatchBody) {
		const url = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/rules/${id}`;

		return this.requestService
			.put({
				url,
				data: body,
			})
			.pipe(
				tap(rule => {
					this.savedRule$.next(rule as MirRuleHttp);
				}),
			);
	}

	getRuleById(ruleId: string, companyId: string) {
		const url = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/rules/${ruleId}?companyId=${companyId}`;
		return this.requestService
			.get<HttpResponse<MirRuleHttp>>({
				url,
				httpOptions: { observe: "response" },
			})
			.pipe(map(res => res.body));
	}

	intToDay(int: number) {
		return this._intsToDaysMap[int];
	}

	translateTimeToHumanReadable(time: TimeRange): string {
		if (!time || time.hr === undefined || time.hr === null || time.min === undefined || time.min === null) {
			return "Invalid time";
		}

		// Convert to 12-hour format
		const hour12 = time.hr === 0 ? 12 : time.hr > 12 ? time.hr - 12 : time.hr;
		const ampm = time.hr >= 12 ? "PM" : "AM";
		const minutes = time.min.toString().padStart(2, "0");

		return `${hour12}:${minutes} ${ampm}`;
	}
	createRule(rule: MirRuleCreateRequest): Observable<HttpResponse<MirRuleHttp>> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/rules`;

		return this.requestService
			.post<HttpResponse<MirRuleHttp>>({
				url,
				data: rule,
				httpOptions: {
					observe: "response",
				},
			})
			.pipe(tap(rule => this.savedRule$.next(rule.body)));
	}

	getAllPages(companyId: string) {
		const uri = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/rules?companyId=${companyId}`;

		return this.serviceUtils.getAllPages(uri, "rules") as Observable<MirRule[]>;
	}

	deleteRules(body: DeleteRulesBody) {
		const url = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/rules/delete`;

		return this.requestService.post({
			url,
			data: body,
		});
	}

	storePatchedRule(divisionId: string, rule: MirRule) {
		if (!divisionId || !rule) {
			return;
		}

		const cloneArray = <T extends DisplayedZone | RuleZone>(items: T[] | undefined | null): T[] =>
			items ? items.map(item => ({ ...item })) : [];

		const cloneTrip = (trip?: TripStartOrEnd): TripStartOrEnd => {
			if (!trip) {
				return trip;
			}

			return {
				...trip,
				zones: cloneArray(trip.zones),
				detailedZones: cloneArray(trip.detailedZones),
			};
		};

		const clonedRule: MirRule = {
			...rule,
			tripStart: cloneTrip(rule.tripStart),
			tripEnd: cloneTrip(rule.tripEnd),
			excludedZones: cloneArray(rule.excludedZones),
			detailedExcludedZones: cloneArray(rule.detailedExcludedZones),
		};

		const existing = this.patchedRuleMap.get(divisionId) ?? {};
		this.patchedRuleMap.set(divisionId, { ...existing, [rule.id]: clonedRule });
	}
}
